import { type BorrowMarketEvent, type BorrowMarketPathParams, getInternalAppSdk } from '@/utils/internalAppSdk'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { MINUTE_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import type { Address } from 'viem'

const borrowMarketEventsQueryKey = ['borrowMarketEvents'] as const

const BORROW_MARKET_EVENTS_PER_PAGE = 1000

interface BorrowMarketEventsQueryParams extends BorrowMarketPathParams {
  address: Address | undefined
  assetId: string
}

function borrowMarketEventsQueryOptions({ address, network, marketId, assetId }: BorrowMarketEventsQueryParams) {
  return queryOptions({
    queryKey: [...borrowMarketEventsQueryKey, { userAddress: address, network, marketId, assetId }] as const,
    async queryFn({ queryKey }) {
      const { userAddress, network, marketId, assetId } = queryKey[1]
      if (!userAddress) return null
      const internalSdk = getInternalAppSdk()
      const events: BorrowMarketEvent[] = []
      let page: number | undefined = 0

      while (page !== undefined) {
        const response = await internalSdk.getBorrowMarketEvents({
          userAddress,
          network,
          marketId,
          assetId,
          page,
          perPage: BORROW_MARKET_EVENTS_PER_PAGE,
        })

        events.push(...response.data)

        if (response.nextPage !== undefined && response.nextPage <= page) {
          break
        }

        page = response.nextPage
      }

      return events
    },
    select(data) {
      if (data == null) {
        return []
      }

      return [...data].sort((a, b) => b.timestamp - a.timestamp)
    },
    enabled: !!address,
    retry: 2,
    staleTime: 5 * MINUTE_IN_MILLISECONDS,
    refetchOnWindowFocus: false,
  })
}

export const useBorrowMarketEventsQuery = (params: BorrowMarketEventsQueryParams) => {
  const {
    data = [],
    isError,
    isFetching,
  } = useQuery({
    ...borrowMarketEventsQueryOptions(params),
  })

  return { data, isError, isFetching }
}
